import { useEffect } from 'react';
import { getConfig } from './api';

export type LiveEvent = { kind: string; [k: string]: unknown };

/**
 * Live updates over WebSocket. Reconnects with backoff, pings every 4 minutes (API Gateway drops idle sockets at 10),
 * and calls onReconnect so the screen can refetch anything it missed while offline.
 */
export function connectLive(token: string, onEvent: (e: LiveEvent) => void, onStatus: (online: boolean) => void, onReconnect: () => void) {
  let ws: WebSocket | undefined;
  let stopped = false;
  let attempt = 0;
  let ping: ReturnType<typeof setInterval> | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;

  const open = async () => {
    const { wsUrl } = await getConfig();
    if (stopped) return;
    ws = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}`);
    ws.onopen = () => {
      if (attempt > 0) onReconnect();
      attempt = 0;
      onStatus(true);
      ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('{"action":"ping"}'), 240_000);
    };
    ws.onmessage = (m) => {
      try {
        const e = JSON.parse(String(m.data)) as LiveEvent;
        if (e.kind !== 'pong') onEvent(e);
      } catch {
        /* ignore malformed */
      }
    };
    ws.onclose = () => {
      clearInterval(ping);
      onStatus(false);
      if (stopped) return;
      attempt++;
      retry = setTimeout(() => void open().catch(() => undefined), Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)));
    };
  };
  void open().catch(() => onStatus(false));
  return () => {
    stopped = true;
    clearTimeout(retry);
    clearInterval(ping);
    ws?.close();
  };
}


/** When the live socket is down (some Wi-Fi blocks WebSockets), refresh on a timer instead. */
export function usePollWhileOffline(online: boolean, refresh: () => void, everyMs = 15_000) {
  useEffect(() => {
    if (online) return;
    const t = setInterval(refresh, everyMs);
    return () => clearInterval(t);
  }, [online, refresh, everyMs]);
}
